import { Flex, Text, Box } from "@chakra-ui/react";
import { BookDemoButton } from "components/event-tracking";
import { HeroCTA } from "components/hero";
import React, { type ReactNode } from "react";
import Balancer from "react-wrap-balancer";
import { ChakraBox } from "ui/chakra-framer";
import { Section, type SectionProps } from "../section";

type HalfHeroProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  animationElement: ReactNode;
  // eslint-disable-next-line react/boolean-prop-naming
  hideSecondary?: boolean;
  backgroundGradient?: ReactNode;
} & SectionProps;

export function HalfHeroAnimation({
  eyebrow,
  title,
  description,
  animationElement,
  hideSecondary,
  backgroundGradient,
  gap,
  ...props
}: HalfHeroProps) {
  return (
    <Section
      pos="relative"
      my={backgroundGradient ? 0 : { base: 12, md: 16 }}
      py={backgroundGradient ? { base: 12, md: 16 } : 0}
      overflow="hidden"
      {...props}
    >
      {backgroundGradient}
      <ChakraBox
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1, transition: { duration: 0.15 } }}
        viewport={{ once: true }}
      >
        <Flex
          align="center"
          justify={{ base: "center", lg: "space-between" }}
          direction={{ base: "column", lg: "row" }}
          gap={gap ?? { base: 12, md: 16, xl: 28 }}
          zIndex={backgroundGradient ? 1 : undefined}
        >
          <Box
            flex={1}
            textAlign={{ base: "center", lg: "left" }}
            maxW={{ base: "3xl", lg: "100%" }}
            mx="auto"
            zIndex={backgroundGradient ? 1 : undefined}
          >
            {eyebrow && (
              <Text fontSize={{ base: "sm", md: "md" }} textStyle="uppercase" mb={4}>
                {eyebrow}
              </Text>
            )}

            <Text as="h1" textStyle={{ base: "h4", md: "h3" }}>
              <Balancer>{title}</Balancer>
            </Text>

            <Text as="h2" fontSize="lg" mt={8} mb={12}>
              {description}
            </Text>

            <Flex justify={{ base: "center", lg: "start" }}>{hideSecondary ? <BookDemoButton /> : <HeroCTA />}</Flex>
          </Box>

          <Flex flex={1} w="100%" justify="center" maxW={{ base: "100%", lg: "50%", xl: "100%" }}>
            {animationElement}
          </Flex>
        </Flex>
      </ChakraBox>
    </Section>
  );
}
